/* ============================================================
   product.js — PDP Web Components
   ProductGallery · VariantPicker · GallerySlider
   ============================================================ */

/* ----------------------------------------------------------
   ProductGallery
   L-shape gallery: 3 stacked images (vertical arm) + row below
   (horizontal arm, rendered by the section, spanning both columns).
   The Web Component is a progressive-enhancement hook for future
   lightbox or gesture support.
   ---------------------------------------------------------- */

class ProductGallery extends HTMLElement {
  #ac = null;
  #mq = null;
  #looping = false;
  #clones = [];
  #realCount = 0;
  #scrollEndTimer = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.stack  = this.querySelector('.product-gallery__stack');
    this.handle = this.querySelector('.product-gallery__progress-handle');
    if (!this.stack) return;

    this.#realCount = this.stack.querySelectorAll('.gallery-stack-item').length;

    this.stack.addEventListener('scroll', () => this.#onScroll(), { signal, passive: true });
    window.addEventListener('resize', () => this.#updateProgress(), { signal });

    // Seamless looping carousel — mobile only (below the desktop stack breakpoint,
    // where the stack is a horizontal swiper). Disabled on desktop so the clones
    // never disturb the vertical-stack :nth-child / :first-child rules.
    this.#mq = window.matchMedia('(max-width: 1023px)');
    this.#mq.addEventListener('change', () => this.#syncLoop(), { signal });
    this.#syncLoop();

    this.#updateProgress();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#scrollEndTimer);
  }

  #onScroll() {
    this.#updateProgress();
    if (!this.#looping) return;
    // Debounced "scroll end": once the swipe settles on a clone, jump to the
    // identical real slide. The clone looks the same, so the jump is invisible.
    clearTimeout(this.#scrollEndTimer);
    this.#scrollEndTimer = setTimeout(() => this.#wrapIfNeeded(), 80);
  }

  #syncLoop() {
    if (this.#mq?.matches && this.#realCount > 1) this.#enableLoop();
    else this.#disableLoop();
  }

  #enableLoop() {
    if (this.#looping) return;
    const items = [...this.stack.querySelectorAll('.gallery-stack-item')];
    const leadClone = items[items.length - 1].cloneNode(true); // copy of last, prepended
    const tailClone = items[0].cloneNode(true);                // copy of first, appended
    for (const c of [leadClone, tailClone]) {
      c.setAttribute('data-clone', '');
      c.setAttribute('aria-hidden', 'true');
    }
    this.stack.insertBefore(leadClone, items[0]);
    this.stack.appendChild(tailClone);
    this.#clones = [leadClone, tailClone];
    this.#looping = true;
    // Start on the first real slide (index 1, after the leading clone).
    requestAnimationFrame(() => { this.#jumpTo(1); this.#updateProgress(); });
  }

  #disableLoop() {
    if (!this.#looping) return;
    for (const c of this.#clones) c.remove();
    this.#clones = [];
    this.#looping = false;
  }

  #itemWidth() {
    return this.stack.clientWidth; // each .gallery-stack-item is flex: 0 0 100%
  }

  // Instant (no smooth) reposition used for the seamless wrap.
  #jumpTo(index) {
    this.stack.scrollTo({ left: index * this.#itemWidth(), behavior: 'auto' });
  }

  #wrapIfNeeded() {
    if (!this.#looping) return;
    const w = this.#itemWidth();
    if (w === 0) return;
    const total = this.#realCount + 2;
    const index = Math.round(this.stack.scrollLeft / w);
    if (index === 0) this.#jumpTo(this.#realCount);          // leading clone → real last
    else if (index === total - 1) this.#jumpTo(1);           // trailing clone → real first
  }

  // Scrollbar-style progress: handle width = visible fraction, left = scroll fraction.
  #updateProgress() {
    if (!this.handle) return;
    const w = this.#itemWidth();
    if (this.#looping && w > 0) {
      // Map the current DOM slide (incl. clones) back to the real image it shows,
      // so the handle reflects the visible image — even while on a clone.
      const total = this.#realCount + 2;
      const i = Math.max(0, Math.min(total - 1, Math.round(this.stack.scrollLeft / w)));
      let real;
      if (i === 0) real = this.#realCount - 1;     // leading clone shows the last image
      else if (i === total - 1) real = 0;          // trailing clone shows the first image
      else real = i - 1;                           // real slides are offset by the leading clone
      this.handle.style.width = `${100 / this.#realCount}%`;
      this.handle.style.left  = `${(real / this.#realCount) * 100}%`;
    } else {
      const { scrollLeft, scrollWidth, clientWidth } = this.stack;
      this.handle.style.width = `${(clientWidth / scrollWidth) * 100}%`;
      this.handle.style.left  = `${(scrollLeft  / scrollWidth) * 100}%`;
    }
  }

  // Called by VariantPicker when a variant is selected.
  showMediaById(mediaId) {
    if (!mediaId) return;
    const img = this.querySelector(`.gallery-stack-item:not([data-clone]) [data-media-id="${mediaId}"]`);
    if (!img) return;
    const item = img.closest('.gallery-stack-item');
    if (!item) return;

    if (this.stack && this.stack.scrollWidth > this.stack.clientWidth) {
      // Mobile: scroll the swiper track horizontally
      this.stack.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
    } else {
      // Desktop: scroll the page to bring the stack item into view
      item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
}

customElements.define('product-gallery', ProductGallery);

/* ----------------------------------------------------------
   VariantPicker
   Color swatch + size button selection.
   Updates: hidden form input, price display, ATC button state,
   gallery image, stock status.
   ---------------------------------------------------------- */

class VariantPicker extends HTMLElement {
  #ac = null;
  #sizeGuideDrawer  = null;
  #sizeGuideTrigger = null;
  #sizeGuideTrap    = null;
  #voyadoMoved      = false;
  #voyadoObserver   = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.variants     = JSON.parse(this.querySelector('script[data-variants]').textContent);
    this.colorIndex   = parseInt(this.dataset.colorIndex ?? '-1', 10);
    this.sizeIndex    = parseInt(this.dataset.sizeIndex  ?? '-1', 10);

    // product.variants | json omits inventory_quantity/inventory_management —
    // merge them in from the separate blob so low-stock detection works on
    // client-side variant switches, not just the initial server render.
    const inventoryEl = this.querySelector('script[data-variant-inventory]');
    if (inventoryEl) {
      const inventoryById = JSON.parse(inventoryEl.textContent);
      this.variants.forEach(v => Object.assign(v, inventoryById[v.id]));
    }

    // Cross-snippet targets (safe — all in the same DOM when page loads)
    this.formInput = document.querySelector('#product-form input[name="id"]');
    this.priceEl   = document.getElementById('product-price');
    this.atcBtn    = document.querySelector('#product-form .product-atc');
    this.atcLabelEl = this.atcBtn?.querySelector('.btn__label');
    this.stockEl   = document.getElementById('product-stock');
    this.notifyEl  = document.querySelector('.product-notify');
    this.notifySubmitBtn = document.querySelector('.product-notify-submit');
    this.galleryEl = document.querySelector('product-gallery');

    // Voyado's BIS app block renders its own container server-side whenever
    // the merchant has added it — its presence (not just "unavailable") is
    // what decides between the "Varsle meg" flow and a plain disabled
    // "Utsolgt" add-to-cart button.
    this.voyadoEnabled = !!document.getElementById('voyado_app_block_bis');

    // Derive initial selections from server-rendered active classes
    this.selectedColor = this.querySelector('.color-swatch--active')?.dataset.value ?? null;
    this.selectedSize  = this.querySelector('.size-btn--active')?.dataset.value ?? null;

    // Initialize size availability for the initial color
    if (this.selectedColor) this.#updateSizeAvailability(this.selectedColor);

    this.querySelectorAll('.color-swatch').forEach(btn =>
      btn.addEventListener('click', () => this.#onColorClick(btn.dataset.value), { signal })
    );
    this.querySelectorAll('.size-btn').forEach(btn =>
      btn.addEventListener('click', () => this.#onSizeClick(btn.dataset.value), { signal })
    );

    // Keyboard support for the ARIA radiogroups (arrow keys / Home / End).
    // Sold-out sizes stay selectable (matches click behavior) so shoppers can
    // still view that variant's price/media; the add-to-cart button is what
    // reflects unavailability.
    this.#initRadioKeys('.variant-picker__swatches', '.color-swatch', signal);
    this.#initRadioKeys('.variant-picker__sizes', '.size-btn', signal);

    // Size guide drawer — portalled to <body> so z-index clears the sticky header
    this.#sizeGuideDrawer  = this.querySelector('[data-size-guide-drawer]');
    this.#sizeGuideTrigger = this.querySelector('[data-size-guide-trigger]');
    if (this.#sizeGuideDrawer) {
      document.body.appendChild(this.#sizeGuideDrawer);
      this.#sizeGuideTrigger?.addEventListener('click', () => this.#openSizeGuide(), { signal });
      this.#sizeGuideDrawer.querySelectorAll('[data-size-guide-close]')
        .forEach(el => el.addEventListener('click', () => this.#closeSizeGuide(), { signal }));
      document.addEventListener('keydown', this.#onSizeGuideKeyDown, { signal });
    }

    this.notifySubmitBtn?.addEventListener('click', () => {
      document.querySelector('#voyado_app_block_bis .voyado-bis-submit')?.click();
    }, { signal });

    // If the page loaded with an already-unavailable variant selected (e.g. a
    // direct ?variant= link), the server already hid .product-atc — relocate
    // Voyado's form now rather than waiting for a variant switch that may
    // never come.
    if (this.atcBtn?.hidden) this.#relocateVoyadoForm();
  }

  disconnectedCallback() {
    if (this.#sizeGuideDrawer?.classList.contains('is-open')) {
      document.body.style.overflow = '';
    }
    this.#sizeGuideTrap?.release({ restoreFocus: false });
    this.#sizeGuideDrawer?.remove();
    this.#voyadoObserver?.disconnect();
    this.#ac?.abort();
  }

  #optionKey(index) {
    return `option${index + 1}`;
  }

  /**
   * Wire arrow-key / Home / End navigation for an ARIA radiogroup, moving
   * focus and selection between radios. Sold-out items stay selectable.
   * @param {string} groupSel   — radiogroup container selector
   * @param {string} itemSel    — radio item selector
   * @param {AbortSignal} signal
   */
  #initRadioKeys(groupSel, itemSel, signal) {
    const group = this.querySelector(groupSel);
    if (!group) return;

    // Guarantee a single tab stop exists even if nothing is pre-selected.
    const all = [...group.querySelectorAll(itemSel)];
    if (!all.some(b => b.getAttribute('tabindex') === '0')) {
      all[0]?.setAttribute('tabindex', '0');
    }

    group.addEventListener('keydown', (e) => {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(e.key)) return;
      const items = [...group.querySelectorAll(itemSel)];
      if (items.length === 0) return;
      e.preventDefault();

      let idx = items.indexOf(document.activeElement);
      if (e.key === 'Home') idx = 0;
      else if (e.key === 'End') idx = items.length - 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') idx = idx <= 0 ? items.length - 1 : idx - 1;
      else idx = idx >= items.length - 1 ? 0 : idx + 1;

      const next = items[idx];
      next.focus();
      next.click(); // reuse existing selection logic (updates aria-checked + tabindex)
    }, { signal });
  }

  #onColorClick(color) {
    this.selectedColor = color;

    // role="radio" buttons use aria-checked (not aria-pressed); keep a single
    // tab stop on the checked one (roving tabindex, ARIA radio pattern).
    this.querySelectorAll('.color-swatch').forEach(btn => {
      const active = btn.dataset.value === color;
      btn.classList.toggle('color-swatch--active', active);
      btn.setAttribute('aria-checked', String(active));
      btn.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.#updateSizeAvailability(color);

    // Auto-select first available size for this color
    const colorKey = this.#optionKey(this.colorIndex);
    const best = this.variants.find(v => v[colorKey] === color && v.available)
              ?? this.variants.find(v => v[colorKey] === color);
    if (best) {
      if (this.sizeIndex >= 0) {
        this.#onSizeClick(best[this.#optionKey(this.sizeIndex)]);
      } else {
        // Color-only product — commit directly
        this.#commitVariant();
      }
    }
  }

  #onSizeClick(size) {
    this.selectedSize = size;

    this.querySelectorAll('.size-btn').forEach(btn => {
      const active = btn.dataset.value === size;
      btn.classList.toggle('size-btn--active', active);
      btn.setAttribute('aria-checked', String(active));
      btn.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.#commitVariant();
  }

  #commitVariant() {
    const colorKey = this.colorIndex >= 0 ? this.#optionKey(this.colorIndex) : null;
    const sizeKey  = this.sizeIndex  >= 0 ? this.#optionKey(this.sizeIndex)  : null;

    const variant = this.variants.find(v =>
      (!this.selectedColor || !colorKey || v[colorKey] === this.selectedColor) &&
      (!this.selectedSize  || !sizeKey  || v[sizeKey]  === this.selectedSize)
    );
    if (!variant) return;

    if (this.formInput) {
      this.formInput.value = variant.id;
      // Third-party embeds (e.g. Voyado Engage's back-in-stock widget) key off
      // the standard variant-id input `change` event and/or the `?variant=`
      // URL param — a direct .value assignment fires neither, so without this
      // they only ever see the variant Shopify picked at page load.
      this.formInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    this.#updatePrice(variant);
    this.#updateAvailability(variant);
    this.#updateGallery(variant);
    TRYEvents.emit('variant:changed', { variantId: variant.id });
  }

  #updateSizeAvailability(color) {
    if (this.colorIndex < 0 || this.sizeIndex < 0) return;
    const colorKey = this.#optionKey(this.colorIndex);
    const sizeKey  = this.#optionKey(this.sizeIndex);

    this.querySelectorAll('.size-btn').forEach(btn => {
      const v = this.variants.find(v =>
        v[colorKey] === color && v[sizeKey] === btn.dataset.value
      );
      btn.classList.toggle('size-btn--unavailable', !v || !v.available);
    });
  }

  #formatMoney(cents) {
    const amount = Math.round(cents / 100).toLocaleString('nb-NO');
    const fmt    = window.__moneyFormat || '{{ amount_no_decimals }} kr';
    // Match every Shopify amount placeholder ({{amount}}, {{amount_no_decimals}},
    // {{amount_with_space_separator}}, etc.) and substitute the space-grouped
    // amount — matching the server-rendered price in snippets/product-price.liquid.
    return fmt.replace(/\{\{[\s-]*amount[a-z_]*[\s-]*\}\}/g, amount);
  }

  #updatePrice(variant) {
    if (!this.priceEl) return;

    // Build DOM nodes directly (no innerHTML) to avoid XSS via __moneyFormat
    const frag = document.createDocumentFragment();

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const current = document.createElement('span');
      current.className = 'product-price__current product-price__current--sale';
      current.textContent = this.#formatMoney(variant.price);
      frag.appendChild(current);

      const compare = document.createElement('s');
      compare.className = 'product-price__compare';
      compare.textContent = this.#formatMoney(variant.compare_at_price);
      frag.appendChild(compare);
    } else {
      const current = document.createElement('span');
      current.className = 'product-price__current';
      current.textContent = this.#formatMoney(variant.price);
      frag.appendChild(current);
    }

    this.priceEl.replaceChildren(frag);
  }

  #updateAvailability(variant) {
    const showNotify = !variant.available && this.voyadoEnabled;
    const atcDisabled = !variant.available && !this.voyadoEnabled;

    if (this.atcBtn) {
      this.atcBtn.hidden = showNotify;
      this.atcBtn.disabled = atcDisabled;
      if (this.atcLabelEl) {
        this.atcLabelEl.textContent = atcDisabled
          ? this.atcBtn.dataset.soldOutText
          : this.atcBtn.dataset.addToCartText;
      }
    }
    if (this.notifySubmitBtn) this.notifySubmitBtn.hidden = !showNotify;
    if (this.notifyEl) this.notifyEl.hidden = !showNotify;

    if (this.stockEl) {
      const showStock = this.stockEl.dataset.showStock === 'true';
      const lowStock = variant.inventory_management === 'shopify'
        && variant.inventory_quantity > 0
        && variant.inventory_quantity <= 1;

      this.stockEl.classList.remove('product-stock--out', 'product-stock--low');

      if (showStock && !variant.available) {
        this.stockEl.classList.add('product-stock--out');
        this.stockEl.textContent = this.stockEl.dataset.soldOutText;
        this.stockEl.hidden = false;
      } else if (showStock && lowStock) {
        this.stockEl.classList.add('product-stock--low');
        this.stockEl.textContent = this.stockEl.dataset.lowStockText;
        this.stockEl.hidden = false;
      } else {
        this.stockEl.hidden = true;
      }
    }

    if (showNotify) this.#relocateVoyadoForm();
  }

  #relocateVoyadoForm() {
    if (this.#voyadoMoved) return;
    const form = document.getElementById('voyado_app_block_bis');
    const slot = document.querySelector('[data-notify-slot]');
    if (!form || !slot) {
      console.warn('[variant-picker] #voyado_app_block_bis or .product-notify slot not found');
      return;
    }
    slot.appendChild(form);
    this.#voyadoMoved = true;

    this.#configureVoyadoForm(form, slot);
  }

  // Voyado's own script populates #voyado_app_block_bis's title/email/checkbox/
  // submit markup client-side after it loads — that render can land before or
  // after this point, so a one-shot querySelector right after relocating the
  // container is a race (this is what caused the placeholder to only show up
  // reliably on a page reload, where Voyado's script happened to already be
  // warm). Configure immediately if the fields are already there, otherwise
  // wait for Voyado to render them in.
  #configureVoyadoForm(form, slot) {
    const apply = () => {
      const checkbox   = form.querySelector('.voyado-bis-checkbox');
      const emailInput = form.querySelector('.voyado-bis-input');
      if (checkbox) checkbox.checked = true;
      if (emailInput) emailInput.placeholder = slot.dataset.emailPlaceholder ?? '';
      return !!(checkbox && emailInput);
    };

    if (apply()) return;

    this.#voyadoObserver = new MutationObserver(() => {
      if (apply()) this.#voyadoObserver?.disconnect();
    });
    this.#voyadoObserver.observe(form, { childList: true, subtree: true });
  }

  #updateGallery(variant) {
    if (!this.galleryEl || !variant.featured_image?.id) return;
    this.galleryEl.showMediaById(String(variant.featured_image.id));
  }

  #openSizeGuide() {
    if (this.#sizeGuideDrawer.classList.contains('is-open')) return;
    this.#sizeGuideDrawer.classList.add('is-open');
    this.#sizeGuideDrawer.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    // Drawer is appended to <body>, so query within the drawer, not `this`.
    this.#sizeGuideTrap = new FocusTrap(this.#sizeGuideDrawer);
    this.#sizeGuideTrap.activate(this.#sizeGuideDrawer.querySelector('[data-size-guide-close]'));
  }

  #closeSizeGuide() {
    this.#sizeGuideDrawer.classList.remove('is-open');
    this.#sizeGuideDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    // Restore focus explicitly to the trigger (it lives outside the drawer).
    this.#sizeGuideTrap?.release({ restoreFocus: false });
    this.#sizeGuideTrap = null;
    this.#sizeGuideTrigger?.focus();
  }

  #onSizeGuideKeyDown = (e) => {
    if (e.key === 'Escape' && this.#sizeGuideDrawer?.classList.contains('is-open')) {
      this.#closeSizeGuide();
    }
  };
}

customElements.define('variant-picker', VariantPicker);

/* ----------------------------------------------------------
   GallerySlider
   Horizontal arm of the L-shape gallery.
   3 images visible at once; prev/next advances one image at a time.
   translateX shifts the continuous flex track by one item step.
   Controls rendered by Liquid only when total images > 3.
   ---------------------------------------------------------- */

class GallerySlider extends HTMLElement {
  #ac = null;
  #index = 0; // index of leftmost visible image

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.track   = this.querySelector('.gallery-slider__track');
    this.inner   = this.querySelector('.gallery-slider__inner');
    this.items   = Array.from(this.querySelectorAll('.gallery-slide-item'));
    this.dots    = Array.from(this.querySelectorAll('.gallery-slider__dot'));
    this.prevBtn = this.querySelector('.gallery-slider__btn--prev');
    this.nextBtn = this.querySelector('.gallery-slider__btn--next');

    // ≤ 3 images: all visible, no navigation needed
    if (this.items.length <= 3) return;

    this.prevBtn?.addEventListener('click', () => this.#goTo(this.#index - 1), { signal });
    this.nextBtn?.addEventListener('click', () => this.#goTo(this.#index + 1), { signal });
    this.dots.forEach(dot =>
      dot.addEventListener('click', () => this.#goTo(parseInt(dot.dataset.index, 10)), { signal })
    );

    window.addEventListener('resize', () => this.#render(), { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #maxIndex() {
    return this.items.length - 3;
  }

  // One step = one item width + its trailing gap = (trackWidth + gap) / 3
  #step() {
    const gap = parseFloat(getComputedStyle(this.inner).columnGap) || 0;
    return (this.track.offsetWidth + gap) / 3;
  }

  #goTo(index) {
    this.#index = Math.max(0, Math.min(index, this.#maxIndex));
    this.#render();
  }

  #render() {
    const idx = this.#index;
    const max = this.#maxIndex;

    this.inner.style.transform = `translateX(${-idx * this.#step()}px)`;

    this.dots.forEach((dot, i) =>
      dot.classList.toggle('gallery-slider__dot--active', i === idx)
    );

    if (this.prevBtn) this.prevBtn.disabled = idx === 0;
    if (this.nextBtn) this.nextBtn.disabled = idx === max;
  }
}

customElements.define('gallery-slider', GallerySlider);

/* ----------------------------------------------------------
   ProductForm
   Intercepts the PDP add-to-cart form submission, posts to
   /cart/add.js, then opens the cart drawer via TRYEvents.
   Falls back to native form submission on network error.
   ---------------------------------------------------------- */

class ProductForm extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const form = this.querySelector('form');
    form?.addEventListener('submit', this.#onSubmit.bind(this), {
      signal: this.#ac.signal,
    });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  // Resolve the Voyado add-to-cart ticket + SKU for the added variant. voyado-slider
  // exposes window.voyadoVariantTicketBySku once the product-page query resolves; we
  // look up the selected variant's SKU from the variant-picker's Shopify variant JSON.
  // That query is async, so the ticket is often not ready at add-to-cart time. We emit
  // the SKU too so voyado-atc.js can poll the lookup briefly before giving up — without
  // it, a slow recs query silently dropped the notification.
  #voyadoLookup(variantId) {
    try {
      const json = document.querySelector('variant-picker script[data-variants]')?.textContent;
      const sku  = (json && JSON.parse(json).find(v => String(v.id) === String(variantId))?.sku) || null;
      if (!sku) return { voyadoTicket: null, voyadoSku: null };
      return { voyadoTicket: window.voyadoVariantTicketBySku?.(sku) ?? null, voyadoSku: sku };
    } catch {
      return { voyadoTicket: null, voyadoSku: null };
    }
  }

  async #onSubmit(e) {
    e.preventDefault();
    const form      = e.currentTarget;
    const variantId = form.querySelector('input[name="id"]')?.value;
    const quantity  = parseInt(form.querySelector('input[name="quantity"]')?.value ?? '1', 10);
    if (!variantId) return;

    const btn = form.querySelector('.product-atc');
    if (btn) btn.setAttribute('aria-busy', 'true');

    try {
      const addResp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity }),
      });
      if (!addResp.ok) throw new Error(`Cart add failed: ${addResp.status}`);

      const cartResp = await fetch('/cart.js');
      const cart     = await cartResp.json();

      const { voyadoTicket, voyadoSku } = this.#voyadoLookup(variantId);
      TRYEvents.emit('cart:item-added', { item_count: cart.item_count, voyadoTicket, voyadoSku });
    } catch (err) {
      console.error('[ProductForm] add error:', err);
      form.submit(); // fall back to native submission
    } finally {
      if (btn) btn.removeAttribute('aria-busy');
    }
  }
}

customElements.define('product-form', ProductForm);

/* ----------------------------------------------------------
   WishlistProxy
   Keeps the theme's own .product-wishlist button as the visible
   UI while forwarding clicks to the hidden Wishlist King app block.
   Mirrors WK's active state back onto the theme button so the
   heart fills when the product is wishlisted.

   Wishlist King mutates class and aria-label on a single <button>.
   Active state = .wk-selected class present on the WK button.

   Elevate favourite notifications are NOT fired here — they live in
   voyado-favorite.js, which listens for Wishlist King's global
   xgen-wishlist-added / xgen-wishlist-removed events so every surface
   (cards, search, sliders, PDP) is covered through one path.
   ---------------------------------------------------------- */

class WishlistProxy {
  #themeBtn      = null;
  #swishBtn      = null;
  #domObserver   = null;
  #stateObserver = null;

  constructor() {
    this.#themeBtn = document.querySelector('.product-wishlist');
    if (!this.#themeBtn) return;

    this.#themeBtn.addEventListener('click', () => this.#swishBtn?.click());
    this.#connectSwish();
  }

  #connectSwish() {
    const container = document.querySelector('[data-swish-product-block]');
    const btn = container?.querySelector('button, a');
    if (container && btn) {
      this.#attach(container, btn);
      return;
    }
    // Watch until both the container and its button are in the DOM
    this.#domObserver = new MutationObserver(() => {
      const c = document.querySelector('[data-swish-product-block]');
      const b = c?.querySelector('button, a');
      if (!c || !b) return;
      this.#domObserver.disconnect();
      this.#domObserver = null;
      this.#attach(c, b);
    });
    this.#domObserver.observe(document.body, { childList: true, subtree: true });
  }

  #attach(container, btn) {
    this.#swishBtn = btn;
    this.#syncState();
    this.#stateObserver = new MutationObserver(() => this.#syncState());
    this.#stateObserver.observe(container, { attributes: true, subtree: true });
  }

  // Mirror WK's active state onto the theme button.
  #syncState() {
    const btn = this.#swishBtn;
    // Wishlist King marks its button with .wk-selected when the product is wishlisted.
    const active =
      btn.classList.contains('wk-selected') ||
      btn.classList.contains('is-active')   ||
      btn.classList.contains('active')      ||
      btn.getAttribute('aria-pressed') === 'true' ||
      btn.getAttribute('aria-checked') === 'true' ||
      btn.dataset.added === 'true';
    this.#themeBtn.classList.toggle('product-wishlist--active', active);
    this.#themeBtn.setAttribute('aria-pressed', String(active));
  }
}

new WishlistProxy();

/* ----------------------------------------------------------
   ProductLightbox
   Full-screen image viewer. Opens when any gallery image is
   clicked; supports wheel + pinch zoom, drag-to-pan when
   zoomed, tap/click to toggle zoom, and prev/next navigation
   (buttons, ← / → keys). Mirrors the drawer a11y contract:
   focus trap, body scroll-lock, Esc to close.
   ---------------------------------------------------------- */

/* Extends HTMLElement (not TRYComponent): product.js is injected by the
   section and executes BEFORE component.js loads, so TRYComponent is undefined
   at class-definition time. FocusTrap is used only inside methods, which run
   later once component.js is available — same pattern as VariantPicker. */
class ProductLightbox extends HTMLElement {
  #ac = null;
  #trap = null;
  #slides = [];
  #index = 0;
  #trigger = null;
  #swipeX = null;

  // Zoom / pan state for the active image.
  #scale = 1;
  #tx = 0;
  #ty = 0;
  static #MIN = 1;
  static #MAX = 4;
  static #STEP_SCALE = 2; // tap-to-zoom target

  // Pointer tracking for pan (1 pointer) and pinch (2 pointers).
  #pointers = new Map();
  #panStart = null;       // { x, y, tx, ty }
  #pinchStartDist = 0;
  #pinchStartScale = 1;
  #moved = false;
  #downX = 0;
  #downY = 0;

  #onKeydown = (e) => {
    if (this.hidden) return;
    if (e.key === 'Escape') this.close();
    else if (e.key === 'ArrowLeft') this.#go(-1);
    else if (e.key === 'ArrowRight') this.#go(1);
  };

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.viewport = this.querySelector('[data-lightbox-viewport]');
    if (!this.viewport) return;
    this.counter  = this.querySelector('[data-lightbox-counter]');
    this.prevBtn  = this.querySelector('[data-lightbox-prev]');
    this.nextBtn  = this.querySelector('[data-lightbox-next]');
    this.#slides  = Array.from(this.querySelectorAll('.product-lightbox__slide'));

    // Open from any gallery image on the page.
    document.addEventListener('click', (e) => {
      const img = e.target.closest('.gallery-stack-item img, .gallery-slide-item img');
      if (!img) return;
      e.preventDefault();
      this.openFromMediaId(img.dataset.mediaId, img);
    }, { signal });

    // Close / navigate via delegated control clicks.
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-lightbox-close]')) this.close();
      else if (e.target.closest('[data-lightbox-prev]')) this.#go(-1);
      else if (e.target.closest('[data-lightbox-next]')) this.#go(1);
    }, { signal });

    document.addEventListener('keydown', this.#onKeydown, { signal });

    // Zoom & pan.
    this.viewport.addEventListener('wheel', (e) => this.#onWheel(e), { signal, passive: false });
    this.viewport.addEventListener('pointerdown',   (e) => this.#onPointerDown(e), { signal });
    this.viewport.addEventListener('pointermove',   (e) => this.#onPointerMove(e), { signal });
    this.viewport.addEventListener('pointerup',     (e) => this.#onPointerUp(e),   { signal });
    this.viewport.addEventListener('pointercancel', (e) => this.#onPointerUp(e),   { signal });

    // Horizontal swipe to change image (only when not zoomed).
    this.viewport.addEventListener('touchstart', (e) => this.#onTouchStart(e), { signal, passive: true });
    this.viewport.addEventListener('touchend',   (e) => this.#onTouchEnd(e),   { signal, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#trap?.release({ restoreFocus: false });
  }

  openFromMediaId(mediaId, trigger) {
    const idx = this.#slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    this.open(idx >= 0 ? idx : 0, trigger);
  }

  open(index, trigger) {
    this.#trigger = trigger || null;
    this.hidden = false;
    document.body.setAttribute('data-lightbox-open', '');
    this.#show(index);
    this.#trap = new FocusTrap(this);
    this.#trap.activate(this.querySelector('[data-lightbox-close]'));
  }

  close() {
    if (this.hidden) return;
    this.hidden = true;
    document.body.removeAttribute('data-lightbox-open');
    this.#resetZoom();
    this.#trap?.release();
    this.#trap = null;
    // Return focus to the image that opened the viewer, when focusable.
    if (typeof this.#trigger?.focus === 'function') this.#trigger.focus();
  }

  get #activeImg() {
    return this.#slides[this.#index]?.querySelector('.product-lightbox__img') ?? null;
  }

  #show(index) {
    const n = this.#slides.length;
    if (n === 0) return;
    this.#index = Math.max(0, Math.min(n - 1, index));
    this.#slides.forEach((s, i) => { s.hidden = i !== this.#index; });
    this.#resetZoom();
    this.#updateCounter();
    this.#updateNav();
  }

  #go(dir) {
    this.#show(this.#index + dir);
  }

  #updateNav() {
    const n = this.#slides.length;
    if (this.prevBtn) this.prevBtn.disabled = this.#index === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.#index === n - 1;
  }

  #updateCounter() {
    if (this.counter) this.counter.textContent = `${this.#index + 1} / ${this.#slides.length}`;
  }

  /* --- Zoom / pan ------------------------------------------ */

  #applyTransform() {
    const img = this.#activeImg;
    if (!img) return;
    img.style.transform = `translate(${this.#tx}px, ${this.#ty}px) scale(${this.#scale})`;
    this.viewport.classList.toggle('is-zoomed', this.#scale > 1);
  }

  #resetZoom() {
    this.#scale = ProductLightbox.#MIN;
    this.#tx = 0;
    this.#ty = 0;
    this.#applyTransform();
  }

  // Zoom toward a viewport-relative point (origin is the viewport centre).
  #zoomTo(nextScale, clientX, clientY) {
    const prev = this.#scale;
    const next = Math.max(ProductLightbox.#MIN, Math.min(ProductLightbox.#MAX, nextScale));
    if (next === prev) return;

    const rect = this.viewport.getBoundingClientRect();
    const px = clientX - rect.left - rect.width / 2;
    const py = clientY - rect.top - rect.height / 2;

    this.#tx = px - (px - this.#tx) * (next / prev);
    this.#ty = py - (py - this.#ty) * (next / prev);
    this.#scale = next;
    this.#clampPan();
    this.#applyTransform();
  }

  // Keep the image from being dragged past its own edges.
  #clampPan() {
    if (this.#scale <= 1) { this.#tx = 0; this.#ty = 0; return; }
    const img = this.#activeImg;
    if (!img) return;
    const maxX = Math.max(0, (img.offsetWidth  * this.#scale - this.viewport.clientWidth)  / 2);
    const maxY = Math.max(0, (img.offsetHeight * this.#scale - this.viewport.clientHeight) / 2);
    this.#tx = Math.max(-maxX, Math.min(maxX, this.#tx));
    this.#ty = Math.max(-maxY, Math.min(maxY, this.#ty));
  }

  #onWheel(e) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    this.#zoomTo(this.#scale * factor, e.clientX, e.clientY);
  }

  #onPointerDown(e) {
    if (!e.target.closest('.product-lightbox__img')) return;
    this.viewport.setPointerCapture?.(e.pointerId);
    this.#pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.#moved = false;
    this.#downX = e.clientX;
    this.#downY = e.clientY;

    if (this.#pointers.size === 2) {
      const [a, b] = [...this.#pointers.values()];
      this.#pinchStartDist = Math.hypot(a.x - b.x, a.y - b.y);
      this.#pinchStartScale = this.#scale;
      this.#panStart = null;
    } else if (this.#scale > 1) {
      this.#panStart = { x: e.clientX, y: e.clientY, tx: this.#tx, ty: this.#ty };
    }
  }

  #onPointerMove(e) {
    if (!this.#pointers.has(e.pointerId)) return;
    this.#pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.#pointers.size === 2 && this.#pinchStartDist > 0) {
      const [a, b] = [...this.#pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      this.#moved = true;
      this.#zoomTo(this.#pinchStartScale * (dist / this.#pinchStartDist), midX, midY);
    } else if (this.#panStart) {
      const dx = e.clientX - this.#panStart.x;
      const dy = e.clientY - this.#panStart.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this.#moved = true;
      this.#tx = this.#panStart.tx + dx;
      this.#ty = this.#panStart.ty + dy;
      this.#clampPan();
      this.#applyTransform();
    }
  }

  #onPointerUp(e) {
    const wasSingle = this.#pointers.size === 1;
    this.#pointers.delete(e.pointerId);
    this.viewport.releasePointerCapture?.(e.pointerId);

    if (this.#pointers.size < 2) { this.#pinchStartDist = 0; }
    if (this.#pointers.size === 0) { this.#panStart = null; }

    // A clean tap/click on the image toggles zoom — but not if the pointer
    // travelled far enough to be a pan or a (not-zoomed) swipe.
    const travel = Math.hypot(e.clientX - this.#downX, e.clientY - this.#downY);
    if (wasSingle && !this.#moved && travel < 10 && e.target.closest('.product-lightbox__img')) {
      const target = this.#scale > 1 ? ProductLightbox.#MIN : ProductLightbox.#STEP_SCALE;
      this.#zoomTo(target, e.clientX, e.clientY);
    }
  }

  /* --- Swipe to change image (only when not zoomed) -------- */

  #onTouchStart(e) {
    if (this.#scale > 1 || e.touches.length !== 1) { this.#swipeX = null; return; }
    this.#swipeX = e.touches[0].clientX;
  }

  #onTouchEnd(e) {
    if (this.#swipeX == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? this.#swipeX) - this.#swipeX;
    this.#swipeX = null;
    if (Math.abs(dx) > 50) this.#go(dx < 0 ? 1 : -1);
  }
}

customElements.define('product-lightbox', ProductLightbox);

